import React from "react";
import WhatsAppFormattedText from "../whatsapp/WhatsAppFormattedText";
import { MessageBubbleButtonFooter } from "../whatsapp/MessageBubbleButtonFooter";
import {
  TemplateBubbleButtons,
  TemplateBubbleButtonRow,
} from "../whatsapp/TemplateBubbleButtons";
import { BubbleTimestampContent } from "./MessageBubbleMeta";

/**
 * Parse raw_payload whether stored as object or JSON string.
 */
export function parseInteractiveRawPayload(rawPayload) {
  if (!rawPayload) return null;
  if (typeof rawPayload === "string") {
    try {
      return JSON.parse(rawPayload);
    } catch {
      return null;
    }
  }
  return rawPayload;
}

export function getInteractiveFromMessage(message) {
  const raw = parseInteractiveRawPayload(message?.raw_payload);
  return raw?.interactive || null;
}

/**
 * Map WhatsApp interactive action buttons to template bubble button shape.
 */
export function mapReplyButtonsToBubbleButtons(buttons = []) {
  return buttons.map((btn, index) => {
    const replyTitle =
      btn.reply?.title || btn.title || btn.text || "Button";
    const replyType = String(btn.type || "reply").toLowerCase();
    let type = "QUICK_REPLY";
    if (replyType === "url") type = "URL";
    if (replyType === "phone_number") type = "PHONE_NUMBER";

    return {
      id: btn.reply?.id || `interactive-btn-${index}`,
      type,
      text: replyTitle,
    };
  });
}

const InteractiveMessage = ({ message, isOutbound, onPreview, metaFooter }) => {
  const interactive = getInteractiveFromMessage(message);
  if (!interactive) {
    return (
      <div className="text-xs opacity-70 italic">Interactive message</div>
    );
  }

  const type = (interactive.type || "").toLowerCase();

  // Inbound: the customer allowed / declined calls
  if (type === "call_permission_reply") {
    const reply = interactive.call_permission_reply || {};
    const accepted = String(reply.response || "").toLowerCase() === "accept";
    return (
      <div className="space-y-0 min-w-0 max-w-full overflow-hidden">
        <BubbleTimestampContent>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-lg leading-none">{accepted ? "✅" : "🚫"}</span>
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-[#111b21]">
                {accepted ? "Calls allowed" : "Calls declined"}
              </p>
              <p className="text-[12px] text-[#667781]">
                {accepted
                  ? "This contact allowed your business to call them on WhatsApp"
                  : "This contact declined calls from your business"}
                {reply.is_permanent === false ? " (temporary)" : ""}
              </p>
            </div>
          </div>
          {metaFooter}
        </BubbleTimestampContent>
      </div>
    );
  }

  // Outbound: the call permission request card
  if (type === "call_permission_request") {
    return (
      <div className="space-y-0 min-w-0 max-w-full overflow-hidden">
        <BubbleTimestampContent>
          {interactive.body?.text && (
            <WhatsAppFormattedText
              text={interactive.body.text}
              className="text-[14.5px] leading-snug whitespace-pre-wrap break-words text-[#111b21] pt-1"
            />
          )}
          {metaFooter}
        </BubbleTimestampContent>
        <MessageBubbleButtonFooter isOutbound={isOutbound}>
          <div className="bg-white rounded-lg border border-[rgba(17,27,33,0.1)] shadow-[0_1px_0.5px_rgba(17,27,33,0.1)] py-2 px-3 text-center">
            <span className="text-[13px] text-[#008069] leading-snug">
              📞 Call permission request
            </span>
          </div>
        </MessageBubbleButtonFooter>
      </div>
    );
  }

  return (
    <div className="space-y-0 min-w-0 max-w-full overflow-hidden">
      <InteractiveHeader
        header={interactive.header}
        isOutbound={isOutbound}
        onPreview={onPreview}
      />

      {(interactive.body?.text || interactive.footer?.text || metaFooter) && (
        <BubbleTimestampContent>
          {interactive.body?.text && (
            <WhatsAppFormattedText
              text={interactive.body.text}
              className="text-[14.5px] leading-snug whitespace-pre-wrap break-words text-[#111b21]"
            />
          )}

          {interactive.footer?.text && (
            <WhatsAppFormattedText
              text={interactive.footer.text}
              className="text-[13px] mt-2 text-[#667781]"
            />
          )}

          {metaFooter}
        </BubbleTimestampContent>
      )}

      {type === "list" && (
        <ListAction action={interactive.action} isOutbound={isOutbound} />
      )}

      {type === "button" && (
        <ButtonAction action={interactive.action} isOutbound={isOutbound} />
      )}

      {type === "cta_url" && (
        <CtaUrlAction action={interactive.action} isOutbound={isOutbound} />
      )}

      {type === "flow" && (
        <FlowAction action={interactive.action} isOutbound={isOutbound} />
      )}

      {type === "carousel" && (
        <CarouselAction
          action={interactive.action}
          isOutbound={isOutbound}
          onPreview={onPreview}
        />
      )}

      {(type === "product" || type === "product_list") && (
        <div className="px-3 py-2 text-xs text-gray-600">
          {type === "product"
            ? `Product · ${interactive.action?.product_retailer_id || ""}`
            : `Product list · ${
                interactive.action?.sections?.[0]?.product_items?.length || 0
              } items`}
        </div>
      )}
    </div>
  );
};

export function hasInteractiveActions(interactive, type) {
  switch (type) {
    case "button":
      return (interactive.action?.buttons?.length || 0) > 0;
    case "cta_url":
    case "flow":
    case "list":
      return Boolean(interactive.action);
    case "carousel":
      return (interactive.action?.cards?.length || 0) > 0;
    case "product":
    case "product_list":
      return Boolean(interactive.action);
    default:
      return false;
  }
}

function ActionButtonFooter({ children, isOutbound }) {
  return (
    <MessageBubbleButtonFooter isOutbound={isOutbound}>
      {children}
    </MessageBubbleButtonFooter>
  );
}

function InteractiveHeader({ header, isOutbound: _isOutbound, inCard = false, onPreview }) {
  if (!header) return null;
  const textClr = "text-[#111b21]";
  const mutedClr = "text-[#667781]";
  const linkClr = "text-[#027eb5]";
  const headerType = (header.type || "").toLowerCase();

  if (headerType === "text") {
    return (
      <WhatsAppFormattedText
        text={header.text}
        className={`text-[15px] font-semibold leading-snug pb-1 ${textClr}`}
      />
    );
  }
  const bleed = inCard
    ? "overflow-hidden"
    : "rounded-t-[6px] overflow-hidden -mx-2.5 sm:-mx-3 -mt-1.5 sm:-mt-2 mb-1.5";
  const imageLink = header.image?.link || header.image?.url;
  if (headerType === "image" && imageLink) {
    return (
      <div className={bleed}>
        <img
          src={imageLink}
          alt="Interactive header"
          onClick={() =>
            onPreview?.({ type: "image", url: imageLink, title: "Image preview" })
          }
          className={`${inCard ? "w-full h-36 object-cover" : "w-full max-h-52 object-cover"} cursor-zoom-in`}
        />
      </div>
    );
  }
  const videoLink = header.video?.link || header.video?.url;
  if (headerType === "video" && videoLink) {
    return (
      <div className={bleed}>
        <video
          src={videoLink}
          controls
          onClick={() =>
            onPreview?.({ type: "video", url: videoLink, title: "Video preview" })
          }
          className={`${inCard ? "w-full h-36 object-cover" : "w-full max-h-52"} cursor-zoom-in`}
        />
      </div>
    );
  }
  if (headerType === "document") {
    const documentLink = header.document?.link || header.document?.url || null;
    return (
      <div className="flex items-center gap-2.5 py-2 px-2 rounded-md bg-[rgba(17,27,33,0.04)] mb-1 border border-[rgba(17,27,33,0.06)]">
        <span className="text-2xl shrink-0">📄</span>
        <div className="min-w-0 flex-1">
          <span
            className={`text-[11px] font-medium uppercase tracking-wide block truncate ${mutedClr}`}
          >
            {header.document?.filename || "Document"}
          </span>
          {documentLink && (
            <button
              type="button"
              onClick={() =>
                onPreview?.({
                  type: "document",
                  url: documentLink,
                  title: header.document?.filename || "Document preview",
                })
              }
              className={`text-xs font-semibold mt-0.5 underline ${linkClr}`}
            >
              Preview document
            </button>
          )}
        </div>
      </div>
    );
  }
  return null;
}

function ListAction({ action, isOutbound }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!action) return null;

  const footerBg = isOutbound ? "bg-[#d9fdd3]" : "bg-white";

  return (
    <div
      className={`mt-2 -mx-2.5 sm:-mx-3 -mb-1.5 sm:-mb-2 px-2 pt-1.5 pb-2 flex flex-col gap-1.5 border-t border-[rgba(17,27,33,0.08)] ${footerBg}`}
    >
      <TemplateBubbleButtonRow
        btn={{
          type: "LIST",
          text: action.button || "View options",
        }}
        onClick={() => setExpanded((v) => !v)}
        variant="chat"
      />

      {expanded && action.sections && (
        <>
          {action.sections.map((section, si) => (
            <div key={si} className="flex flex-col gap-1.5">
              {section.title && (
                <div className="text-[10px] font-semibold uppercase tracking-wide px-1 text-[#54656f]">
                  {section.title}
                </div>
              )}
              {section.rows?.map((row, ri) => (
                <ListOptionPill
                  key={row.id || `${si}-${ri}`}
                  title={row.title}
                  description={row.description}
                />
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/** List menu row — same pill shape as action buttons */
function ListOptionPill({ title, description }) {
  return (
    <div
      className="bg-white rounded-lg border border-[rgba(17,27,33,0.1)] shadow-[0_1px_0.5px_rgba(17,27,33,0.1)] py-2 px-3 text-center"
    >
      <div className="text-[13px] text-[#008069] leading-snug truncate">
        {title || "Option"}
      </div>
      {description && (
        <div className="text-[11px] text-[#667781] mt-0.5 leading-snug break-words">
          {description}
        </div>
      )}
    </div>
  );
}

function ButtonAction({ action, isOutbound }) {
  const bubbleButtons = mapReplyButtonsToBubbleButtons(action?.buttons);
  if (!bubbleButtons.length) return null;

  return (
    <ActionButtonFooter isOutbound={isOutbound}>
      <TemplateBubbleButtons
        buttons={bubbleButtons}
        variant="chat"
        showTopBorder={false}
      />
    </ActionButtonFooter>
  );
}

function CtaUrlAction({ action, isOutbound }) {
  if (!action) return null;
  const displayText =
    action.display_text ||
    action.parameters?.display_text ||
    action.parameters?.title ||
    action.name ||
    "Visit website";

  return (
    <ActionButtonFooter isOutbound={isOutbound}>
      <TemplateBubbleButtons
        buttons={[{ type: "URL", text: displayText, id: "cta-url" }]}
        variant="chat"
        showTopBorder={false}
      />
    </ActionButtonFooter>
  );
}

function FlowAction({ action, isOutbound }) {
  if (!action) return null;
  const label =
    action.parameters?.flow_cta || action.name || "Open";

  return (
    <ActionButtonFooter isOutbound={isOutbound}>
      <TemplateBubbleButtons
        buttons={[{ type: "FLOW", text: label, id: "flow-cta" }]}
        variant="chat"
        showTopBorder={false}
      />
    </ActionButtonFooter>
  );
}

function CarouselAction({ action, isOutbound, onPreview }) {
  const trackRef = React.useRef(null);
  const cards = Array.isArray(action?.cards) ? action.cards : [];
  if (cards.length === 0) return null;

  const cardBorder = "border-[rgba(17,27,33,0.12)]";
  const titleColor = "text-[#111b21]";
  const subColor = "text-[#667781]";
  const navBtnBg =
    "bg-[rgba(17,27,33,0.06)] hover:bg-[rgba(17,27,33,0.1)] text-[#111b21]";

  const scrollByOneCard = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const cardEl = el.querySelector("[data-carousel-card]");
    const cardWidth = cardEl
      ? cardEl.getBoundingClientRect().width + 8
      : Math.max(180, Math.round(el.clientWidth * 0.78));
    const left =
      direction === "next" ? el.scrollLeft + cardWidth : el.scrollLeft - cardWidth;
    el.scrollTo({ left, behavior: "smooth" });
  };

  return (
    <div className="mt-2 pt-2 border-t border-[rgba(17,27,33,0.1)] w-full min-w-0 max-w-full overflow-hidden">
      <div className="flex items-center justify-between mb-2 gap-2 min-w-0">
        <p className={`text-[11px] font-medium shrink-0 ${subColor}`}>
          {cards.length} cards
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => scrollByOneCard("prev")}
            className={`p-1 rounded ${navBtnBg}`}
            aria-label="Previous carousel cards"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={() => scrollByOneCard("next")}
            className={`p-1 rounded ${navBtnBg}`}
            aria-label="Next carousel cards"
          >
            ›
          </button>
        </div>
      </div>

      <div className="w-full min-w-0 max-w-full overflow-hidden">
        <div
          ref={trackRef}
          className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1 snap-x snap-mandatory scrollbar-thin max-w-full touch-pan-x"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
        {cards.map((card, idx) => {
          const bodyText = card?.body?.text || "";
          const cardAction = card?.action || {};
          const cardType = (card?.type || "").toLowerCase();
          const isCardCta = cardType === "cta_url";
          const ctaText =
            cardAction.display_text ||
            cardAction.parameters?.display_text ||
            cardAction.name ||
            "View";

          return (
            <div
              key={card.card_index ?? idx}
              data-carousel-card
              className={`rounded-lg border overflow-hidden bg-white ${cardBorder} flex-none w-[180px] max-w-[180px] snap-start flex flex-col`}
            >
              <InteractiveHeader
                header={card.header}
                isOutbound={isOutbound}
                inCard
                onPreview={onPreview}
              />

              <div className="px-2.5 py-2 flex-1 flex flex-col">
                {bodyText && (
                  <WhatsAppFormattedText
                    text={bodyText}
                    className={`text-xs whitespace-pre-wrap break-words ${titleColor}`}
                  />
                )}

                {isCardCta && (
                  <div className="mt-2 -mx-2.5 overflow-hidden rounded-b-[4px] border-t border-[#dadde1] bg-white">
                    <TemplateBubbleButtons
                      buttons={[
                        {
                          type: "URL",
                          text: ctaText,
                          id: `carousel-cta-${idx}`,
                        },
                      ]}
                      variant="chat"
                      showTopBorder={false}
                    />
                  </div>
                )}

                <div className="mt-auto pt-2">
                  <p className={`mt-1 text-[10px] ${subColor}`}>
                    Card {idx + 1} of {cards.length}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

export default InteractiveMessage;
